import React from 'react';

import { CircularProgress, Tab, Tabs } from '@mui/material';

import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

import debounce from 'lodash/debounce';

import API from 'utils/api';

import { PreviewCounts, PreviewStatus, RuleRequest } from 'utils/api/modules/memberGroups';

import { MEMBER_SEARCH_THRESHOLD } from 'containers/MemberGroups/utils/membership';
import { useMemberSearch } from 'containers/MemberGroups/utils/useMemberSearch';
import MemberSearchField from 'containers/MemberGroups/components/MemberSearchField';
import { conditionHasValue, RuleDraft, serializeRule } from '../RuleBuilder/registry';
import PreviewMemberTable from './PreviewMemberTable';
import {
  PreviewErrorBox,
  PreviewMessage,
  PreviewPlaceholder,
  PreviewRoot,
  RetryButton,
  SummaryJoining,
  SummaryLeaving,
  SummaryLine,
  SummarySeparator,
  SummaryTotal,
} from './styles';

const PREVIEW_DEBOUNCE_MS = 400;

export interface RulePreviewProps {
  rule: RuleDraft;
  groupUuid?: string;
}

const previewableRule = (rule: RuleDraft): RuleRequest | null => {
  const complete = rule.conditions.filter(conditionHasValue);
  if (complete.length === 0) {
    return null;
  }
  return serializeRule({ combinator: rule.combinator, conditions: complete });
};

const RulePreview: React.FunctionComponent<RulePreviewProps> = ({ rule, groupUuid }) => {
  const [counts, setCounts] = React.useState<PreviewCounts | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [status, setStatus] = React.useState<PreviewStatus>('all');
  const [sentRule, setSentRule] = React.useState<RuleRequest | null>(null);
  const { searchQuery, temporaryFilters, onSearchChange, resetSearch } = useMemberSearch();

  const showBreakdown = !!groupUuid;

  React.useEffect(() => {
    resetSearch();
  }, [status, resetSearch]);

  const wireRule = previewableRule(rule);
  const ruleKey = wireRule ? JSON.stringify(wireRule) : '';

  const fetchCounts = React.useCallback(
    (payloadRule: RuleRequest, isLive: () => boolean) => {
      setLoading(true);
      setError(false);
      const body = groupUuid ? { rule: payloadRule, group: groupUuid } : { rule: payloadRule };
      API.memberGroups
        .previewCounts(body)()
        .then((response) => {
          if (!isLive()) return;
          setCounts(response.data);
          setSentRule(payloadRule);
          setLoading(false);
        })
        .catch(() => {
          if (!isLive()) return;
          setError(true);
          setLoading(false);
        });
    },
    [groupUuid]
  );

  const debouncedFetch = React.useMemo(() => debounce(fetchCounts, PREVIEW_DEBOUNCE_MS), [fetchCounts]);

  React.useEffect(() => {
    if (!ruleKey) {
      setCounts(null);
      setSentRule(null);
      setError(false);
      return undefined;
    }
    let live = true;
    debouncedFetch(JSON.parse(ruleKey) as RuleRequest, () => live);
    return () => {
      live = false;
      debouncedFetch.cancel();
    };
  }, [ruleKey, debouncedFetch]);

  React.useEffect(() => {
    if (!counts) {
      setStatus('all');
      return;
    }
    if ((status === 'joining' && counts.joining === 0) || (status === 'leaving' && counts.leaving === 0)) {
      setStatus('all');
    }
  }, [counts, status]);

  if (!ruleKey) {
    return (
      <PreviewPlaceholder>
        <GroupsOutlinedIcon />
        Choose a value to see who matches.
      </PreviewPlaceholder>
    );
  }

  if (error) {
    return (
      <PreviewErrorBox>
        Couldn&apos;t load the preview.
        <RetryButton type="button" onClick={() => fetchCounts(JSON.parse(ruleKey) as RuleRequest, () => true)}>
          Retry
        </RetryButton>
      </PreviewErrorBox>
    );
  }

  if (loading && !counts) {
    return (
      <SummaryLine>
        <CircularProgress size={16} />
      </SummaryLine>
    );
  }

  if (!counts || !sentRule) {
    return null;
  }

  if (counts.all === 0 && counts.leaving === 0) {
    return (
      <PreviewRoot>
        <SummaryLine>
          <SummaryTotal>0 will be members</SummaryTotal>
          {loading && <CircularProgress size={14} />}
        </SummaryLine>
        <PreviewMessage>
          No one matches this rule. You can still save it. Users join the group when they match.
        </PreviewMessage>
      </PreviewRoot>
    );
  }

  const activeCount = status === 'joining' ? counts.joining : status === 'leaving' ? counts.leaving : counts.all;

  return (
    <PreviewRoot>
      <SummaryLine>
        <SummaryTotal>{counts.all} will be members</SummaryTotal>
        {showBreakdown && counts.joining > 0 && (
          <>
            <SummarySeparator>·</SummarySeparator>
            <SummaryJoining>{counts.joining} joining</SummaryJoining>
          </>
        )}
        {showBreakdown && counts.leaving > 0 && (
          <>
            <SummarySeparator>·</SummarySeparator>
            <SummaryLeaving>{counts.leaving} leaving</SummaryLeaving>
          </>
        )}
        {loading && <CircularProgress size={14} />}
      </SummaryLine>

      {showBreakdown && (
        <Tabs
          value={status}
          onChange={(_, value: PreviewStatus) => setStatus(value)}
          aria-label="Rule result breakdown"
        >
          <Tab label={`All (${counts.all})`} value="all" />
          {counts.joining > 0 && <Tab label={`Joining (${counts.joining})`} value="joining" />}
          {counts.leaving > 0 && <Tab label={`Leaving (${counts.leaving})`} value="leaving" />}
        </Tabs>
      )}

      {counts.all === 0 && status === 'all' ? (
        <PreviewMessage>No one matches this rule yet.</PreviewMessage>
      ) : (
        <>
          {activeCount >= MEMBER_SEARCH_THRESHOLD && (
            <MemberSearchField value={searchQuery} onChange={onSearchChange} />
          )}
          <PreviewMemberTable
            key={status}
            rule={sentRule}
            status={status}
            groupUuid={groupUuid}
            temporaryFilters={temporaryFilters}
          />
        </>
      )}
    </PreviewRoot>
  );
};

export default RulePreview;
